import { Fragment, useMemo } from "react";

import { abbrFloatingNumberFilter, percentageSignOnlyFilter } from "app/filters/ngFilters";
import type {
    ChartDataFormat,
    CompareChartGraphDataItem,
} from "app/pages/sales-intelligence/modules/ai-agent/services/chart/types";

import {
    StyledBar,
    StyledGraphAxis,
    StyledOpportunityGraphContent,
    StyledValueContainer,
    StyledValueText,
    StyledWebsiteName,
    StyleSiteContainer,
} from "./styles";

const truncateText = (text: string, maxLength: number) => {
    if (typeof text !== "string") {
        return "";
    }
    if (text.length <= maxLength + 1) {
        return text;
    }
    return text.slice(0, maxLength).trim() + "…";
};

type CompareChartProps = {
    chartData: CompareChartGraphDataItem[];
    chartWidth?: number;
    isSimplifiedView?: boolean;
    format?: ChartDataFormat;
};

export const CompareChart = ({
    chartData: chart,
    chartWidth = 110,
    isSimplifiedView = false,
    format = "number",
}: CompareChartProps) => {
    const hasNegativeValue = useMemo(() => chart.some((d) => d.value < 0), [chart]);
    const hasOnlyNegativeValues = useMemo(() => chart.every((d) => d.value < 0), [chart]);

    const barWidth = (value: number) => {
        const biggestValue = chart[0].value;
        const smallestValue = chart.at(-1)?.value ?? 0;
        if (!hasNegativeValue) {
            return (chartWidth / biggestValue) * value;
        }

        if (hasOnlyNegativeValues) {
            return (chartWidth / Math.abs(smallestValue)) * Math.abs(value);
        }

        return Math.round((chartWidth / (biggestValue - smallestValue)) * Math.abs(value));
    };

    const originAt = () => {
        if (!hasNegativeValue) {
            return 0;
        }
        return barWidth(chart.at(-1)?.value ?? 0);
    };

    const getFormattedValue = (value: number): number | string => {
        if (format === "percentage") {
            return value > 5000 ? " > 5,000%" : percentageSignOnlyFilter()(value, 2);
        }
        if (!format || format === "number" || format === "currency" || format === "raw") {
            return value < 10
                ? (abbrFloatingNumberFilter()(value, false, 2) as string)
                : (abbrFloatingNumberFilter()(value) as string);
        }
        return `${value}${format}`;
    };

    return (
        <StyledOpportunityGraphContent isSimplifiedView={isSimplifiedView}>
            {chart.map(({ name, value, favicon, isHighlighted }) => (
                <Fragment key={name}>
                    {isSimplifiedView ? (
                        <StyleSiteContainer hasIcon={!!favicon}>
                            {favicon && isHighlighted ? <img src={favicon} /> : null}
                        </StyleSiteContainer>
                    ) : (
                        <StyleSiteContainer hasIcon={!!favicon}>
                            {favicon ? <img src={favicon} /> : null}
                            <StyledWebsiteName isHighlighted={isHighlighted}>
                                {truncateText(name, 10)}
                            </StyledWebsiteName>
                        </StyleSiteContainer>
                    )}
                    <StyledValueContainer>
                        <StyledBar
                            width={barWidth(value)}
                            isHighlighted={isHighlighted}
                            isNegative={value < 0}
                            originAt={originAt()}
                        />
                        <StyledValueText isHighlighted={isHighlighted}>{getFormattedValue(value)}</StyledValueText>
                    </StyledValueContainer>
                </Fragment>
            ))}
            {hasNegativeValue ? (
                <StyledGraphAxis
                    left={originAt()}
                    isSimplifiedView={isSimplifiedView}
                />
            ) : null}
        </StyledOpportunityGraphContent>
    );
};
